import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { Lock, ArrowRight, AlertCircle } from "lucide-react";
import { useAdminAuth, formatApiErrorDetail } from "../../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const { login, token, bootChecked } = useAdminAuth();
  const navigate = useNavigate();
  const [question, setQuestion] = useState("");
  const [form, setForm] = useState({ username: "", password: "", security_answer: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/security-question`).then(({ data }) => setQuestion(data?.question || "")).catch(() => {});
  }, []);

  if (bootChecked && token) return <Navigate to="/admin" replace />;

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(form);
      navigate("/admin");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-cream-100 text-ink flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-baseline gap-1">
            <span className="font-serif text-3xl">in</span>
            <span className="font-serif italic text-3xl text-terracotta-400">Touch</span>
          </div>
          <p className="mt-3 text-xs uppercase tracking-[0.24em] text-ink/55">Admin</p>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="surface-card p-7 md:p-9 space-y-4"
        >
          <div className="flex items-center gap-2 text-ink/70 mb-2">
            <Lock className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-xs uppercase tracking-[0.22em] font-medium">Yönetici Girişi</span>
          </div>

          <Field label="Kullanıcı adı">
            <input
              data-testid="admin-username"
              type="text"
              autoComplete="username"
              required
              value={form.username}
              onChange={update("username")}
              className="form-input"
            />
          </Field>

          <Field label="Şifre">
            <input
              data-testid="admin-password"
              type="password"
              autoComplete="current-password"
              required
              value={form.password}
              onChange={update("password")}
              className="form-input"
            />
          </Field>

          <Field label={question || "Güvenlik sorusu"}>
            <input
              data-testid="admin-security-answer"
              type="text"
              autoComplete="off"
              required
              value={form.security_answer}
              onChange={update("security_answer")}
              className="form-input"
            />
          </Field>

          {err && (
            <div data-testid="admin-login-error" className="flex items-start gap-2 text-sm text-terracotta-500 bg-terracotta-50 border border-terracotta-100 rounded-xl p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{err}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-ink/50">
          5 hatalı denemeden sonra hesap 15 dakika boyunca kilitlenir.
        </p>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #FBFAF6;
          border: 1px solid #E5E0D8;
          border-radius: 0.85rem;
          padding: 0.75rem 0.95rem;
          font-family: 'Outfit', sans-serif;
          font-size: 0.95rem;
          color: #2C3531;
          font-weight: 300;
          transition: all 0.2s ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #839788;
          background: #FFFFFF;
          box-shadow: 0 0 0 4px rgba(131, 151, 136, 0.12);
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.22em] text-ink/55 font-medium mb-2">{label}</span>
      {children}
    </label>
  );
}
